import React from "react";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx-js-style";

const applyFormats = (worksheet, formats) => {
  if (!worksheet["!ref"]) return;

  const range = XLSX.utils.decode_range(worksheet["!ref"]);

  for (let col = range.s.c; col <= range.e.c; col++) {
    const headerAddress = XLSX.utils.encode_cell({
      r: 0,
      c: col,
    });

    const headerCell = worksheet[headerAddress];

    if (!headerCell) continue;

    const columnName = String(headerCell.v);
    const format = formats[columnName];

    if (!format) continue;

    for (let row = 1; row <= range.e.r; row++) {
      const cellAddress = XLSX.utils.encode_cell({
        r: row,
        c: col,
      });

      const cell = worksheet[cellAddress];

      if (!cell) continue;

      // Support existing string formats
      // Example: "0.00%", "#,##0.00"
      if (typeof format === "string") {
        cell.z = format;
      }

      // Support object formats
      if (typeof format === "object") {
        if (format.numFmt) {
          cell.z = format.numFmt;
        }

        if (format.fill) {
          cell.s = {
            ...(cell.s || {}),
            fill: format.fill,
          };
        }
      }
    }
  }
};

const applyConditionalColumnStyles = (worksheet, columnStyles) => {
  if (!worksheet["!ref"] || !columnStyles) return;

  const range = XLSX.utils.decode_range(worksheet["!ref"]);

  // Map Excel headers to column indexes
  const headerColumnMap = {};

  for (let col = range.s.c; col <= range.e.c; col++) {
    const headerAddress = XLSX.utils.encode_cell({
      r: 0,
      c: col,
    });

    const headerCell = worksheet[headerAddress];

    if (headerCell) {
      headerColumnMap[String(headerCell.v)] = col;
    }
  }

  // Apply style to the complete column
  Object.entries(columnStyles).forEach(([columnName, style]) => {
    const columnIndex = headerColumnMap[columnName];

    if (columnIndex === undefined) {
      console.log("Style column not found:", columnName);
      return;
    }

    for (let row = range.s.r; row <= range.e.r; row++) {
      const cellAddress = XLSX.utils.encode_cell({
        r: row,
        c: columnIndex,
      });

      const cell = worksheet[cellAddress];

      if (!cell) continue;

      cell.s = {
        ...(cell.s || {}),
        ...style,
      };
    }
  });
};

export const JsonToExcelFormatted = ({
  data,
  title,
  fileName,
  btnClassName,
  btnColor = "#4CAF50",
  multipleSheets,
  formats = {},
  columnStyles = {},
}) => {
  const downloadExcel = () => {
    const workbook = {
      Sheets: {},
      SheetNames: [],
    };

    const createWorksheet = (sheetData) => {
      // Keep the original data because it contains __styles
      const originalData = Array.isArray(sheetData) ? sheetData : [];

      const worksheet = XLSX.utils.json_to_sheet(originalData);

      // Apply normal column formats
      applyFormats(worksheet, formats);

      // Apply dynamic per-cell conditional styles
      applyConditionalColumnStyles(worksheet, columnStyles);

      return worksheet;
    };

    if (multipleSheets) {
      data.forEach(({ sheetName, details }) => {
        const worksheet = createWorksheet(details);

        workbook.Sheets[sheetName] = worksheet;
        workbook.SheetNames.push(sheetName);
      });
    } else {
      const worksheet = createWorksheet(data);

      workbook.Sheets.data = worksheet;
      workbook.SheetNames.push("data");
    }

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
      cellStyles: true,
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    saveAs(blob, `${fileName}.xlsx`);
  };

  return (
    <div>
      <button
        className={`${btnClassName || ""} download-button`}
        onClick={downloadExcel}
        style={{ background: btnColor }}
      >
        {title || "Download"}
      </button>
    </div>
  );
};
